// React
import React, { useCallback, useState, useEffect } from 'react';

// utils
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import messages from 'aurora-translations';
import { keyEmptyCheck } from 'aurora-utils/utils';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useTheme from '@material-ui/core/styles/useTheme';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

// Components
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import FormControl from '@material-ui/core/FormControl';
import Modal from '@material-ui/core/Modal';
import Alert from '@material-ui/lab/Alert';
import { ADGMImage } from 'aurora-components';
import PropertyBadge from '../ADGMPropertiesV2/PropertyList/Badge';

// Styles
import stylesHeader from './stylesHeader';

const CTAButton = ({ variant, className, size, onClick, label, btnClasses, classes, ...props }) => (
  <Button variant={variant} className={classNames(btnClasses, className)} size={size} onClick={onClick} {...props}>
    {keyEmptyCheck(label)}
  </Button>
);
// eslint-disable-next-line react/prop-types
function ApprovalHeaderV2(props) {
  const {
    classes,
    property,
    failure,
    intl,
    locale,
    applicationType,
    applicationNumber,
    showApplicationHeader = false,
    customHeaderContent = false,
    primaryBtnLabel,
    primaryBtnHandler,
    handleRejectApplication,
    handleReturnApplication,
    behavior,
    returnTitle,
    rejectTitle,
    returnRequired,
    rejectRequired,
    returnModalHintText,
    renderCustomButtons,
    rejectModalHintText,
    modalTextAreaMaxLength,
    customModalBody,
    handleCancelApplication,
  } = props;

  const [inputVal, setInputVal] = useState('');
  const [RejectModalOpened, setRejectModalOpened] = useState(false);
  const [ReturnModalOpened, setReturnModalOpened] = useState(false);
  const [SmallScreensReturnRejectSection, setSmallScreensReturnRejectSection] = useState(false);

  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));

  const inputHandler = useCallback(event => {
    setInputVal(event.target.value);
  }, []);

  const toggleRejectModal = useCallback(() => {
    setRejectModalOpened(prev => !prev);
    setInputVal('');
  }, []);

  const toggleReturnModal = useCallback(() => {
    setReturnModalOpened(prev => !prev);
    setInputVal('');
  }, []);

  const toggleReturnRejectUpoveBtnShow = useCallback(() => {
    setSmallScreensReturnRejectSection(prev => !prev);
  }, []);

  useEffect(() => {
    if (!mobileView) {
      setSmallScreensReturnRejectSection(false);
    }
  }, [mobileView]);

  const UNIT = property?.propertyType === 4;
  const PLOT = property?.propertyType === 1;
  const ImageSrc = PLOT ? 'land_property.svg' : 'PropertyDetails-Unit.svg';
  const propertiesKeysMap = {
    ar: {
      community: 'communityAr',
      district: 'districtAr',
      plotNumber: 'plotNumber',
      unitNumber: 'unitNumber',
      buildingNumber: 'buildingNumber',
    },
    en: {
      community: 'communityEn',
      district: 'districtEn',
      plotNumber: 'plotNumber',
      unitNumber: 'unitNumber',
      buildingNumber: 'buildingNumber',
    },
  };
  const { community, district, plotNumber, unitNumber, buildingNumber } = propertiesKeysMap[locale];

  const [isDownloadMenuOpen, setDownloadMenuOpen] = useState(null);

  const handleClose = () => {
    setDownloadMenuOpen(null);
  };

  const renderCTAButtons = useCallback(() => {
    switch (behavior) {
      case 'ApproveReject':
        return (
          <>
            <CTAButton
              variant='outlined'
              className={classes.rejectBtn}
              size='large'
              onClick={toggleRejectModal}
              label='reject_application'
              classes={classes}
              startIcon={<ADGMImage name='roundedCloseIcon.svg' alt='close' />}
            />
            <CTAButton
              variant='contained'
              size='large'
              onClick={primaryBtnHandler}
              label={primaryBtnLabel}
              classes={classes}
            />
          </>
        );
      case 'ApproveReturn':
      case 'ApproveRejectReturn':
        return mobileView && behavior === 'ApproveRejectReturn' ? (
          <>
            <CTAButton
              variant='outlined'
              className={classes.rejectBtnMenu}
              size='large'
              onClick={toggleReturnRejectUpoveBtnShow}
              label='return_reject'
              classes={classes}
              startIcon={<ADGMImage name='arrow-up-black.svg' alt='arrow-up-black' />}
            />
            <CTAButton
              variant='contained'
              className={classes.accptBtn}
              size='large'
              color='primary'
              onClick={primaryBtnHandler}
              label={primaryBtnLabel}
              classes={classes}
            />
          </>
        ) : (
          <>
            <Button
              aria-controls='download-menu'
              aria-haspopup='true'
              onClick={e => {
                setDownloadMenuOpen(e.currentTarget);
              }}
              variant='outlined'
              className={classes.rejectBtnMenu}
              endIcon={
                <ADGMImage name={isDownloadMenuOpen ? 'arrow-up-black.svg' : 'arrow-down-black.svg'} alt='return' />
              }
              size='large'
            >
              {keyEmptyCheck('return_reject')}
            </Button>
            <Menu
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              id='download-menu'
              anchorEl={isDownloadMenuOpen}
              open={Boolean(isDownloadMenuOpen)}
              onClose={() => setDownloadMenuOpen(null)}
              getContentAnchorEl={null}
              classes={{ paper: classes.navMenuDropdown, list: classes.menuList }}
            >
              <MenuItem
                onClick={() => {
                  toggleReturnModal();
                  handleClose();
                }}
                variant='outlined'
                className={classes.rejectBtnMenu}
                endIcon={
                  <ADGMImage name={isDownloadMenuOpen ? 'arrow-up-black.svg' : 'arrow-down-black.svg'} alt='return' />
                }
                size='large'
              >
                {keyEmptyCheck('return_with_feedback')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  toggleRejectModal();
                  handleClose();
                }}
              >
                {keyEmptyCheck('reject_with_feedback')}
              </MenuItem>
            </Menu>

            <CTAButton
              className={classes.accptBtn}
              variant='contained'
              size='large'
              onClick={primaryBtnHandler}
              label={primaryBtnLabel}
              classes={classes}
              color='primary'
            />
          </>
        );
      case 'Approve':
        return (
          <CTAButton
            variant='contained'
            className={classes.accptBtn}
            color='primary'
            size='large'
            onClick={primaryBtnHandler}
            label={primaryBtnLabel}
            classes={classes}
          />
        );
      case 'EditCancel':
        return (
          <>
            <CTAButton
              variant='outlined'
              className={classes.cancelBtn}
              size='large'
              onClick={handleCancelApplication}
              label='cancel_application'
              classes={classes}
              startIcon={<ADGMImage name='roundedCloseIcon.svg' alt='close' />}
            />
            <CTAButton
              variant='contained'
              className={classes.editBtn}
              size='large'
              onClick={primaryBtnHandler}
              label='edit_application'
              classes={classes}
            />
          </>
        );
      case 'EditTerminate':
        return (
          <>
            <CTAButton
              variant='outlined'
              className={classes.rejectBtn}
              size='large'
              onClick={handleCancelApplication}
              label='terminate'
              classes={classes}
            />
            <CTAButton
              className={classes.accptBtn}
              variant='contained'
              size='large'
              onClick={primaryBtnHandler}
              label={primaryBtnLabel}
              classes={classes}
              color='primary'
            />
          </>
        );
      default:
        return null;
    }
  }, [behavior, mobileView, isDownloadMenuOpen]);

  return (
    <>
      {failure?.statusCode && (
        <Alert severity='error' className={classes.errorMessage}>
          {keyEmptyCheck(failure?.message)}
        </Alert>
      )}

      <Grid container spacing={2} alignItems='center' className={classes.initiateserviceSect}>
        {customHeaderContent ? (
          <Grid item xs={12} md={6}>
            {customHeaderContent}
          </Grid>
        ) : (
          !showApplicationHeader && (
            <Grid item xs={12} md={6} className={classes.headerContainer}>
              <Grid className={classes.propertyDetailsContainer}>
                <Grid item className={classes.imgCls}>
                  <ADGMImage name={ImageSrc} alt={ImageSrc} className={classes.headerImage} />
                </Grid>
                <Grid item>
                  <Typography variant='h6' className={classes.title}>
                    {UNIT ? (
                      <span>
                        {keyEmptyCheck('Unit')} {property?.[unitNumber]}, {keyEmptyCheck('building')}{' '}
                        {property?.[buildingNumber]}{' '}
                      </span>
                    ) : (
                      <span>
                        {keyEmptyCheck('Plot')} {property?.[plotNumber]}
                      </span>
                    )}
                    <div className={classes.chip}>{keyEmptyCheck(PLOT ? 'Plot' : 'Unit')}</div>
                  </Typography>

                  <Typography variant='h6' className={classes.subInfo}>
                    {property?.[community]}, {property?.[district]}
                  </Typography>
                  <Typography variant='h6' className={classes.badgeContainer}>
                    <PropertyBadge
                      locale={locale}
                      property={property}
                      isOwnership={false}
                      isLease
                      expanded={false}
                      handleExpand={() => {}}
                    />
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )
        )}

        {showApplicationHeader && (
          <Grid item xs={12} md={6} className={classes.appDeatailsHeader}>
            <Typography variant='h6' component='h1' className={classes.Title}>
              {keyEmptyCheck(applicationType)}
            </Typography>
            <Typography variant='body1' className={classes.subTitle}>
              {keyEmptyCheck('Application_ID')}
              {': '}
              {applicationNumber}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={6} align='right' className={classes.CTAButtons}>
          {renderCustomButtons ?? renderCTAButtons()}
        </Grid>

        {SmallScreensReturnRejectSection && (
          <div className={classes.CTARejectReturnAboveBtnsSection}>
            <></>
            {behavior === 'ApproveRejectReturn' && (
              <>
                <CTAButton
                  className={classNames(classes.AboveSectionBtns, classes.return)}
                  onClick={toggleReturnModal}
                  label='return_with_feedback'
                  classes={classes}
                  startIcon={<ADGMImage name='returnArrow.svg' alt='returnArrow' />}
                />

                <CTAButton
                  onClick={toggleRejectModal}
                  className={classNames(classes.AboveSectionBtns, classes.reject)}
                  label='reject_application'
                  classes={classes}
                  startIcon={<ADGMImage name='closeRed.svg' alt='closeRed' />}
                />
              </>
            )}
          </div>
        )}
      </Grid>

      {/* Return Modal */}
      <ModalGenerator
        title={returnTitle}
        isRequired={returnRequired}
        open={ReturnModalOpened}
        toggleModal={toggleReturnModal}
        onClose={toggleReturnModal}
        intl={intl}
        textAreaPlaceholder={messages.return_reason}
        inputHandler={inputHandler}
        inputVal={inputVal}
        hint={returnModalHintText}
        actionHandler={handleReturnApplication}
        actionText='submit_return'
        classes={classes}
        modalTextAreaMaxLength={modalTextAreaMaxLength}
        customModalBody={customModalBody}
      />

      {/* Reject Modal V2 */}
      <ModalGenerator
        title={rejectTitle}
        isRequired={rejectRequired}
        open={RejectModalOpened}
        toggleModal={toggleRejectModal}
        onClose={toggleRejectModal}
        intl={intl}
        textAreaPlaceholder={messages.reject_reason}
        inputHandler={inputHandler}
        inputVal={inputVal}
        hint={rejectModalHintText}
        actionHandler={handleRejectApplication}
        actionText='submit_reject_V2'
        classes={classes}
        modalTextAreaMaxLength={modalTextAreaMaxLength}
      />
    </>
  );
}

export const ModalGenerator = withStyles(stylesHeader)(
  ({
    classes,
    title,
    open,
    toggleModal,
    onClose,
    intl,
    textAreaPlaceholder,
    inputHandler,
    inputVal,
    hint,
    actionHandler,
    actionText,
    isRequired,
    modalTextAreaMaxLength,
    customModalBody,
  }) => {
    return (
      <Modal
        aria-labelledby='simple-modal-title'
        aria-describedby='simple-modal-description'
        open={open}
        toggleModal={toggleModal}
        onClose={onClose}
      >
        <div className={classNames(classes.modal, classes.modalpaper)}>
          <div className={classes.modalHeader}>
            <Typography variant='h6' component='h1' className={classes.tenantRejectTitleV2}>
              {keyEmptyCheck(title)} {isRequired && <span className={classes.requiredRed}>*</span>}
            </Typography>
            <ADGMImage
              name='cross.svg'
              alt='crossicon'
              className={classes.crossIconimg}
              onClick={toggleModal}
              aria-hidden='true'
            />
          </div>
          <FormControl className={classes.formControl}>
            <div
              htmlFor='PropertyID'
              className={classNames([
                classes.unitinputControl,
                inputVal?.length === modalTextAreaMaxLength && classes.colorRed,
              ])}
            >
              <TextareaAutosize
                aria-label='minimum height'
                minRows={1}
                placeholder={intl.formatMessage({ ...textAreaPlaceholder })}
                onChange={inputHandler}
                value={inputVal}
                maxLength={modalTextAreaMaxLength}
              />
              <div className={classes.hintBar}>
                {hint && <p className={classes.returnHintText}>{keyEmptyCheck(hint)}</p>}
                {modalTextAreaMaxLength && (
                  <p>
                    {inputVal?.length}/{modalTextAreaMaxLength}
                  </p>
                )}
              </div>
            </div>
          </FormControl>
          {customModalBody && customModalBody()}
          <div className={classNames(classes.feedbackmodalBtnholder)}>
            <Button
              variant='contained'
              className={classNames(classes.btns, classes.submitBtnV2)}
              size='large'
              onClick={() => actionHandler(inputVal)}
              disabled={!inputVal}
            >
              {keyEmptyCheck(actionText)}
            </Button>
          </div>
        </div>
      </Modal>
    );
  },
);

ApprovalHeaderV2.propTypes = {
  classes: PropTypes.object.isRequired,
  property: PropTypes.object,
  failure: PropTypes.object,
  intl: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  applicationType: PropTypes.string.isRequired,
  applicationNumber: PropTypes.string.isRequired,
  showApplicationHeader: PropTypes.bool.isRequired,
  primaryBtnLabel: PropTypes.string,
  primaryBtnHandler: PropTypes.func,
  handleRejectApplication: PropTypes.func,
  handleReturnApplication: PropTypes.func,
  behavior: PropTypes.string.isRequired,
  returnModalHintText: PropTypes.string,
  renderCustomButtons: PropTypes.node,
  rejectModalHintText: PropTypes.string,
  modalTextAreaMaxLength: PropTypes.number,
  returnTitle: PropTypes.string,
  rejectTitle: PropTypes.string,
  returnRequired: PropTypes.bool,
  rejectRequired: PropTypes.bool,
  customHeaderContent: PropTypes.bool,
  customModalBody: PropTypes.func,
  handleCancelApplication: PropTypes.func,
};

ModalGenerator.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  textAreaPlaceholder: PropTypes.object.isRequired,
  inputHandler: PropTypes.func.isRequired,
  inputVal: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  actionHandler: PropTypes.func.isRequired,
  actionText: PropTypes.string.isRequired,
  modalTextAreaMaxLength: PropTypes.number.isRequired,
  isRequired: PropTypes.bool.isRequired,
  customModalBody: PropTypes.func.isRequired,
};

ApprovalHeaderV2.defaultProps = {
  property: {},
  application: {},
  failure: {},
  primaryBtnLabel: '',
  primaryBtnHandler: () => {},
  handleRejectApplication: () => {},
  handleReturnApplication: () => {},
  returnRequired: true,
  rejectRequired: true,
  customHeaderContent: null,
  returnModalHintText: 'max_text_text',
  renderCustomButtons: null,
  rejectModalHintText: 'max_text_text',
  modalTextAreaMaxLength: 500,
  returnTitle: 'return_reason',
  rejectTitle: 'reject_reason',
  customModalBody: () => {},
  handleCancelApplication: () => {},
};

CTAButton.propTypes = {
  variant: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  size: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  btnClasses: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(stylesHeader)(injectIntl(ApprovalHeaderV2));
